import { useEffect, useRef, useState } from "react";
import { Loader2, Upload, FileUp } from "lucide-react";
import { toast } from "sonner";

import { runService } from "@/services/runService";
import { ACCEPTED_FILE_EXTENSIONS, MAX_FILE_MB } from "@/config/runOptions";
import { Field } from "@/components/profile/FormControls";
import { RelatedEntityPicker } from "@/components/run/RelatedEntityPicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const NONE = "__none__";

/** Upload a private document. `presetRelated` locks the linked record. */
export const UploadDocumentDialog = ({ open, onOpenChange, presetRelated, presetFolderId, onUploaded }) => {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [folders, setFolders] = useState([]);
  const [folderId, setFolderId] = useState(NONE);
  const [related, setRelated] = useState(null);
  const [progress, setProgress] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setFile(null); setProgress(0); setRelated(presetRelated || null);
      setFolderId(presetFolderId || NONE);
      runService.listFolders().then(setFolders).catch(() => {});
    }
  }, [open, presetRelated, presetFolderId]);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_FILE_MB * 1024 * 1024) { toast.error(`File exceeds the ${MAX_FILE_MB} MB limit`); return; }
    setFile(f);
  };

  const upload = async () => {
    if (!file) return;
    setSaving(true); setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (folderId !== NONE) fd.append("folder_id", folderId);
      if (related?.type && related?.id) { fd.append("related_entity_type", related.type); fd.append("related_entity_id", related.id); }
      await runService.uploadDocument(fd, setProgress);
      toast.success("Document uploaded");
      onUploaded?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="upload-document-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Upload className="h-5 w-5 text-accent" /> Upload Document</DialogTitle>
          <DialogDescription>Keep important files connected to your business. Stored privately — never shared publicly.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <button type="button" onClick={() => inputRef.current?.click()} data-testid="doc-dropzone"
            className="flex w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed border-border bg-secondary/40 px-6 py-8 text-center transition-colors hover:border-accent hover:bg-secondary">
            <FileUp className="h-7 w-7 text-accent" />
            <span className="text-sm font-medium text-foreground">{file ? file.name : "Choose a file to upload"}</span>
            <span className="text-xs text-muted-foreground">PDF, DOC, DOCX, XLS, XLSX, JPG, PNG · up to {MAX_FILE_MB} MB</span>
          </button>
          <input ref={inputRef} type="file" accept={ACCEPTED_FILE_EXTENSIONS} className="hidden" onChange={onPick} data-testid="doc-file-input" />

          <Field label="Folder (optional)" htmlFor="doc-folder">
            <Select value={folderId} onValueChange={setFolderId}>
              <SelectTrigger id="doc-folder" data-testid="doc-folder"><SelectValue placeholder="No folder" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>No folder</SelectItem>
                {folders.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Link to (optional)"><RelatedEntityPicker value={related} onChange={setRelated} locked={!!presetRelated} /></Field>

          {saving && <Progress value={progress} className="h-2" data-testid="doc-upload-progress" />}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="upload-doc-cancel">Cancel</Button>
          <Button onClick={upload} disabled={saving || !file} data-testid="upload-doc-save">
            {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Uploading… {progress}%</> : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
